<template>
  <div class="grid-separator-container">
    <hr class="grid-separator" />
    <p class="grid-separator-title">Parcourir les travaux d'un élève</p>
    <a-select
      class="grid-separator-select"
      :class="{ 'dark-theme-select': darkTheme }"
      v-model="defaultClassroom"
      showSearch
      placeholder="Classe"
      optionFilterProp="children"
      style="width: 200px"
      @change="handleClassroomChange"
      :filterOption="filterOption"
    >
      <a-select-option v-for="classroom in classroomsData" :key="classroom">{{classroom}}</a-select-option>
    </a-select>
    <a-select
      class="grid-separator-select"
      :class="{ 'display-none': studentSelectInvisible, 'dark-theme-select': darkTheme }"
      v-model="defaultStudent"
      showSearch
      placeholder="Élève"
      optionFilterProp="children"
      style="width: 200px"
      @change="handleStudentChange"
      :filterOption="filterOption"
    >
      <div slot="dropdownRender" slot-scope="menu">
        <v-nodes :vnodes="menu"/>
        <a-divider style="margin: 4px 0;" />
        <div class="btn-print-classroom" @mousedown="handleShowPrintClassroomWorkModal">
          <a-icon type="printer" /> Imprimer tout
        </div>
      </div>
      <a-select-option v-for="student in studentsData" :key="student">{{student}}</a-select-option>
    </a-select>
    <a-tooltip placement="top" style="visibility: visible;">
      <template slot="title">
        <span>Imprimer les travaux de l'élève sélectionné</span>
      </template>
      <a-button
        class="print-btn"
        :class="{ 'display-none': studentSelectInvisible }"
        :ghost="darkTheme ? true : null"
        @click="handleShowPrintStudentWorkModal"
        >Imprimer</a-button>
    </a-tooltip>
    <PrintModal @clicked="handlePrintClassroomWorks" :loading="printModalLoading" rangeType="thisWeek" type="classroom-works" modalTitle="Imprimer tous les travaux de la classe" />
    <PrintModal @clicked="handlePrintStudentWorks" :loading="printModalLoading" rangeType="thisWeek" type="students-works" modalTitle="Imprimer les travaux d'un élève" />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import PrintModal from '@/components/modal/PrintModal.vue'
import LdapService from '../services/ldap_service'
import WorkService from '../services/work_service'
import DateService from '../services/date_service'
import PrintService from '../services/print_service'

export default {
  name: 'SelectGridSeparator',
  components: {
    PrintModal,
    VNodes: {
      functional: true,
      render: (h, ctx) => ctx.props.vnodes
    }
  },
  data () {
    return {
      printModalLoading: false
    }
  },
  methods: {
    ...mapActions({
      'togglePrintStudentWorksModalVisible': 'modal/togglePrintStudentWorksModalVisible',
      'togglePrintClassroomWorksModalVisible': 'modal/togglePrintClassroomWorksModalVisible',
      'setWorksData': 'work/setWorksData',
      'sortWorksNonChronological': 'work/sortWorksNonChronological',
      'setLoading': 'work/setLoading',
      'setClassroomsData': 'ldap/setClassroomsData',
      'setStudentsData': 'ldap/setStudentsData',
      'addStudentsData': 'ldap/addStudentsData',
      'setStudentsDataMapLogin': 'ldap/setStudentsDataMapLogin',
      'setStudentSelectInvisible': 'ldap/setStudentSelectInvisible',
      'setDefaultClassroom': 'ldap/setDefaultClassroom',
      'setDefaultStudent': 'ldap/setDefaultStudent'
    }),
    async handleClassroomChange (value) {
      const response = await LdapService.getStudents(value)
      if (response) {
        const data = response.data.data
        this.setStudentsData([])
        this.setStudentsDataMapLogin([])
        this.setStudentsDataMapLogin(data)
        data.forEach(element => {
          this.addStudentsData(element.fullname)
        })
        this.setStudentSelectInvisible(false)
        this.setDefaultClassroom(value)
        this.setDefaultStudent(this.studentsData[0])
        this.handleStudentChange(this.defaultStudent)
      }
    },
    async handleStudentChange (value) {
      // Récupération du login de l'utilisateur sélectionné
      const element = this.studentsDataMapLogin.find((element) => element.fullname === value)
      const login = element.login
      this.setLoading(true)
      const response = await WorkService.getWorks(login)
      if (response) {
        const data = DateService.formatObjectArrayDatesToDisplayFormat(response.data.data, 'display')
        this.setWorksData(data)
        this.sortWorksNonChronological()
        this.setLoading(false)
      }
    },
    handleShowPrintClassroomWorkModal () {
      console.log('modal')
      this.togglePrintClassroomWorksModalVisible()
    },
    async handlePrintClassroomWorks (values) {
      console.log('printing classroom works')
      this.printModalLoading = true
      const fromData = values.dateRange[0].format('YYYY-MM-DD')
      const toData = values.dateRange[1].format('YYYY-MM-DD')
      const pdf = await PrintService.getClassroomDetailsPdf(this.studentsDataMapLogin, fromData, toData, values.sort, values.includeComments)
      if (pdf) {
        pdf.output('dataurlnewwindow')
      }
      this.printModalLoading = false
    },
    handleShowPrintStudentWorkModal () {
      this.togglePrintStudentWorksModalVisible()
    },
    async handlePrintStudentWorks (values) {
      console.log('printing students works')
      this.printModalLoading = true
      // L'enseignant imprime les travaux de l'élève
      const element = this.studentsDataMapLogin.find((element) => element.fullname === this.defaultStudent)
      const login = element.login
      const fromData = values.dateRange[0].format('YYYY-MM-DD')
      const toData = values.dateRange[1].format('YYYY-MM-DD')
      const pdf = await PrintService.getWorksDetailsPdf(login, fromData, toData, values.sort, values.includeComments)
      if (pdf) {
        pdf.output('dataurlnewwindow')
      }
      this.printModalLoading = false
    },
    filterOption (input, option) {
      return option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0
    }
  },
  computed: {
    ...mapState({
      darkTheme: state => state.darkTheme,
      classroomsData: state => state.ldap.classroomsData,
      studentsData: state => state.ldap.studentsData,
      studentsDataMapLogin: state => state.ldap.studentsDataMapLogin,
      studentSelectInvisible: state => state.ldap.studentSelectInvisible
    }),
    defaultStudent: {
      get () {
        return this.$store.state.ldap.defaultStudent
      },
      set (value) {
        this.setDefaultStudent(value)
      }
    },
    defaultClassroom: {
      get () {
        return this.$store.state.ldap.defaultClassroom
      },
      set (value) {
        this.setDefaultClassroom(value)
      }
    }
  },
  async created () {
    console.log('fetch classrooms')
    const response = await LdapService.getWorkClassrooms()
    if (response) {
      const data = response.data.data
      // Supprime les doublons
      this.setClassroomsData([...new Set(data)])
    }
  }
}
</script>

<style scoped lang="scss">
.grid-separator-container{
  margin-bottom: 20px;
}
.grid-separator-title{
  font-size: 17px;
  font-weight: bold;
}
.grid-separator-select, .grid-separator-title, .print-btn{
  margin-left: 10px;
  margin-bottom: 10px;
}
.grid-separator{
  margin-top: 0;
  border: none;
  height: 1px;
  color: lightgray;
  background-color: lightgray;
}
.btn-print-classroom{
  padding: 8px;
  cursor: pointer;
}
</style>
